/**
 * GovDocumentsPage — Reusable document library.
 * Capability statements, past performance, insurance certs, bond letters,
 * boilerplate proposal sections. Attachable to any solicitation's bid packet.
 * Route: /gov/documents
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import { S, GOV_COLORS, DOC_CATEGORIES } from './govShared';

interface LibDoc {
  id: string;
  name: string;
  description?: string;
  category: string;
  storage_url?: string;
  storage_path?: string;
  file_name?: string;
  file_size_bytes?: number;
  mime_type?: string;
  version?: string;
  is_current?: boolean;
  tags?: string[];
  naics_codes?: string[];
  set_asides?: string[];
  expires_at?: string;
  expiration_reminder_days?: number;
  expiration_status?: 'ok' | 'expiring_soon' | 'expired' | null;
  uploaded_by?: string;
  created_at?: string;
  updated_at?: string;
}

const emptyDoc = (): Partial<LibDoc> => ({
  name: '', category: 'capability_statement', version: 'v1', is_current: true,
  tags: [], naics_codes: [], set_asides: [], expiration_reminder_days: 30,
});

function humanSize(bytes?: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

export default function GovDocumentsPage() {
  const [docs, setDocs] = useState<LibDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState<LibDoc | null>(null);
  const [linkedSols, setLinkedSols] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<Partial<LibDoc>>(emptyDoc());
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (catFilter !== 'all') params.set('category', catFilter);
      if (search) params.set('search', search);
      params.set('current_only', showArchived ? 'false' : 'true');
      const { data } = await api.get(`/gov/documents-library?${params.toString()}`);
      setDocs(data?.documents || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [catFilter, search, showArchived]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const openDoc = async (d: LibDoc) => {
    setSelected(d);
    try {
      const { data } = await api.get(`/gov/documents-library/${d.id}`);
      setLinkedSols(data?.linked_solicitations || []);
    } catch { setLinkedSols([]); }
  };

  const openNewModal = () => { setForm(emptyDoc()); setFile(null); setShowModal(true); };
  const openEditModal = () => { if (selected) { setForm({ ...selected }); setFile(null); setShowModal(true); } };

  const saveDoc = async () => {
    if (!form.name?.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const isEdit = !!(form as any).id;
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/gov/documents-library/${(form as any).id}`, form);
        saved = data?.document;
      } else {
        // Use multipart when a file is attached; JSON otherwise
        if (file) {
          const fd = new FormData();
          Object.entries(form).forEach(([k, v]) => {
            if (v == null) return;
            if (Array.isArray(v)) fd.append(k, JSON.stringify(v));
            else fd.append(k, String(v));
          });
          fd.append('file', file);
          const { data } = await api.post('/gov/documents-library', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          saved = data?.document;
        } else {
          const { data } = await api.post('/gov/documents-library', form);
          saved = data?.document;
        }
      }
      setShowModal(false);
      await fetchDocs();
      if (saved) setSelected(saved);
    } catch (e: any) {
      alert(e?.response?.data?.error || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const deleteDoc = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.name}"? File will be removed from storage.`)) return;
    try {
      await api.delete(`/gov/documents-library/${selected.id}`);
      setSelected(null);
      await fetchDocs();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Delete failed');
    }
  };

  const catMeta = (k: string) => DOC_CATEGORIES.find(c => c.key === k) || DOC_CATEGORIES[DOC_CATEGORIES.length - 1];

  const grouped = useMemo(() => {
    const by: Record<string, LibDoc[]> = {};
    docs.forEach(d => { (by[d.category] ||= []).push(d); });
    return DOC_CATEGORIES.filter(c => by[c.key]?.length).map(c => ({ meta: c, items: by[c.key] }));
  }, [docs]);

  const expiringSoon = docs.filter(d => d.expiration_status === 'expiring_soon' || d.expiration_status === 'expired');

  return (
    <div style={S.container}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>📎 Documents Library</h1>
          <p style={S.subtitle}>{docs.length} documents · reusable bid packet assets</p>
        </div>
        <button onClick={openNewModal} style={S.addBtn}>+ Upload Document</button>
      </div>

      {expiringSoon.length > 0 && (
        <div style={{ ...S.card, borderLeft: `4px solid ${GOV_COLORS.warning}`, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: GOV_COLORS.warning }}>
            ⚠️ {expiringSoon.length} document{expiringSoon.length > 1 ? 's' : ''} need{expiringSoon.length === 1 ? 's' : ''} attention
          </div>
          <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, marginTop: 4 }}>
            {expiringSoon.map(d => d.name).join(' · ')}
          </div>
        </div>
      )}

      <div style={S.toolbar}>
        <input type="text" placeholder="🔍 Search name or description…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ ...S.input, minWidth: 320 }} />
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={S.select}>
          <option value="all">All categories ({docs.length})</option>
          {DOC_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: GOV_COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          Show archived versions
        </label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 400px' : '1fr', gap: 16 }}>
        <div>
          {loading ? (
            <div style={S.loading}>Loading documents…</div>
          ) : docs.length === 0 ? (
            <div style={S.empty}>No documents yet. Upload your capability statement, insurance certs, and bond letters.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {grouped.map(g => (
                <div key={g.meta.key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 4, height: 18, background: GOV_COLORS.accent, borderRadius: 2 }} />
                    <h3 style={{ ...S.panelTitle, marginBottom: 0 }}>{g.meta.icon} {g.meta.label} ({g.items.length})</h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {g.items.map(d => {
                      const expChip = d.expiration_status === 'expired'
                        ? { text: 'EXPIRED', color: GOV_COLORS.danger }
                        : d.expiration_status === 'expiring_soon'
                        ? { text: `EXPIRES ${d.expires_at?.substring(5, 10)}`, color: GOV_COLORS.warning }
                        : null;
                      return (
                        <div key={d.id} onClick={() => openDoc(d)} style={{
                          ...S.listRow,
                          background: selected?.id === d.id ? GOV_COLORS.cardHi : GOV_COLORS.card,
                        }}>
                          <div style={{ fontSize: 22 }}>{g.meta.icon}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>
                              {d.name}
                              {!d.is_current && <span style={{ ...S.chip, background: GOV_COLORS.textDim as any + '22', color: GOV_COLORS.textMuted, marginLeft: 8, fontSize: 10 }}>archived</span>}
                            </div>
                            <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, marginTop: 2 }}>
                              {d.version && <span>{d.version} · </span>}
                              {humanSize(d.file_size_bytes)}
                              {d.file_name && <span> · {d.file_name}</span>}
                            </div>
                          </div>
                          {expChip && (
                            <div style={{ ...S.chip, background: expChip.color + '22', color: expChip.color }}>{expChip.text}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div style={{ ...S.card, alignSelf: 'flex-start', position: 'sticky', top: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{selected.name}</div>
                <div style={{ ...S.chip, background: GOV_COLORS.accent + '22', color: GOV_COLORS.accent, marginTop: 6 }}>
                  {catMeta(selected.category).icon} {catMeta(selected.category).label}
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: GOV_COLORS.textMuted, cursor: 'pointer', fontSize: 20 }}>×</button>
            </div>

            {selected.description && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>Description</div>
                <div style={{ ...S.kvValue, fontSize: 13, whiteSpace: 'pre-wrap' }}>{selected.description}</div>
              </div>
            )}
            {selected.version && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>Version</div>
                <div style={S.kvValue}>{selected.version}{selected.is_current ? ' (current)' : ' (archived)'}</div>
              </div>
            )}
            {selected.expires_at && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>Expires</div>
                <div style={{
                  ...S.kvValue,
                  color: selected.expiration_status === 'expired' ? GOV_COLORS.danger
                       : selected.expiration_status === 'expiring_soon' ? GOV_COLORS.warning
                       : '#fff',
                }}>{selected.expires_at}</div>
              </div>
            )}
            {selected.tags && selected.tags.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>Tags</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {selected.tags.map(t => (
                    <span key={t} style={{ ...S.chip, background: GOV_COLORS.accent + '22', color: GOV_COLORS.accent }}>{t}</span>
                  ))}
                </div>
              </div>
            )}
            {selected.naics_codes && selected.naics_codes.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>NAICS</div>
                <div style={S.kvValue}>{selected.naics_codes.join(', ')}</div>
              </div>
            )}
            {selected.file_name && (
              <div style={{ marginBottom: 10 }}>
                <div style={S.kvLabel}>File</div>
                <div style={{ ...S.kvValue, fontSize: 13 }}>
                  {selected.storage_url ? (
                    <a href={selected.storage_url} target="_blank" rel="noreferrer" style={{ color: GOV_COLORS.accent, fontWeight: 600 }}>
                      📄 {selected.file_name} ({humanSize(selected.file_size_bytes)})
                    </a>
                  ) : (
                    <span>{selected.file_name} ({humanSize(selected.file_size_bytes)})</span>
                  )}
                </div>
              </div>
            )}

            {linkedSols.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={S.kvLabel}>Referenced by ({linkedSols.length}) solicitation{linkedSols.length > 1 ? 's' : ''}</div>
                {linkedSols.map((l: any) => (
                  <a key={l.solicitation.id} href={`/gov/solicitations/${l.solicitation.id}`} style={{
                    display: 'block', padding: '8px 10px', background: '#0F1923', borderRadius: 6,
                    marginTop: 6, textDecoration: 'none', color: GOV_COLORS.text, fontSize: 12,
                  }}>
                    <div style={{ fontWeight: 700 }}>{l.solicitation.solicitation_number || '—'}</div>
                    <div style={{ color: GOV_COLORS.textMuted }}>{l.solicitation.title}</div>
                  </a>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={openEditModal} style={{ ...S.addBtn, flex: 1 }}>Edit</button>
              <button onClick={deleteDoc} style={S.dangerBtn}>Delete</button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div style={S.modalBackdrop} onClick={() => setShowModal(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={S.modalTitle}>{(form as any).id ? 'Edit Document' : 'Upload Document'}</h2>

            <div style={S.modalRow}>
              <label style={S.modalLabel}>Name *</label>
              <input type="text" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                style={{ ...S.input, width: '100%' }} placeholder="MB Capability Statement 2026" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Category *</label>
                <select value={form.category || 'other'} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} style={{ ...S.select, width: '100%' }}>
                  {DOC_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
                </select>
              </div>
              <div>
                <label style={S.modalLabel}>Version</label>
                <input type="text" value={form.version || ''} onChange={e => setForm(p => ({ ...p, version: e.target.value }))}
                  style={{ ...S.input, width: '100%' }} placeholder="v1 · 2026-Q3" />
              </div>
            </div>

            <div style={S.modalRow}>
              <label style={S.modalLabel}>Description</label>
              <textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2}
                style={{ ...S.input, width: '100%', resize: 'vertical' as const, minHeight: 60 }} />
            </div>

            {!(form as any).id && (
              <div style={S.modalRow}>
                <label style={S.modalLabel}>File</label>
                <input type="file" ref={fileInputRef} onChange={e => setFile(e.target.files?.[0] || null)}
                  style={{ ...S.input, width: '100%', padding: '8px 12px' }} />
                {file && <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, marginTop: 4 }}>{file.name} · {humanSize(file.size)}</div>}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>Expires</label>
                <input type="date" value={form.expires_at || ''} onChange={e => setForm(p => ({ ...p, expires_at: e.target.value }))} style={{ ...S.input, width: '100%' }} />
              </div>
              <div>
                <label style={S.modalLabel}>Reminder Days</label>
                <input type="number" value={form.expiration_reminder_days || 30} onChange={e => setForm(p => ({ ...p, expiration_reminder_days: Number(e.target.value) }))} style={{ ...S.input, width: '100%' }} />
              </div>
            </div>

            <div style={S.modalRow}>
              <label style={S.modalLabel}>Tags (comma-separated)</label>
              <input type="text" value={(form.tags || []).join(', ')}
                onChange={e => setForm(p => ({ ...p, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                style={{ ...S.input, width: '100%' }} placeholder="roofing, sitework, design-build" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={S.modalLabel}>NAICS Codes</label>
                <input type="text" value={(form.naics_codes || []).join(', ')}
                  onChange={e => setForm(p => ({ ...p, naics_codes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                  style={{ ...S.input, width: '100%' }} placeholder="238160, 238170" />
              </div>
              <div>
                <label style={S.modalLabel}>Set-Asides</label>
                <input type="text" value={(form.set_asides || []).join(', ')}
                  onChange={e => setForm(p => ({ ...p, set_asides: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                  style={{ ...S.input, width: '100%' }} placeholder="8a, hubzone, sdvosb" />
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: GOV_COLORS.textMuted, fontSize: 13, cursor: 'pointer', marginBottom: 14 }}>
              <input type="checkbox" checked={!!form.is_current} onChange={e => setForm(p => ({ ...p, is_current: e.target.checked }))} />
              Current version (uncheck to archive)
            </label>

            <div style={S.modalFooter}>
              <button onClick={() => setShowModal(false)} style={{ ...S.cancelBtn, flex: 1 }}>Cancel</button>
              <button onClick={saveDoc} disabled={saving} style={{ ...S.addBtn, flex: 2, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : ((form as any).id ? 'Save Document' : 'Upload Document')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
